import { useRef } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import Tooltip from "@mui/material/Tooltip";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
} from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

// Registrar componentes de Chart.js
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement
);

/**
 * Modal para mostrar gráficos de reportes
 * Soporta gráficos de barras, líneas y torta
 */
function ModalGrafico({
  abierto = false,
  onCerrar,
  titulo = "Gráfico",
  tipoGrafico = "bar",
  datosGrafico = {},
  opcionesPersonalizadas = {},
}) {
  const chartRef = useRef(null);

  // Opciones por defecto según el tipo de gráfico
  const opcionesPorDefecto = {
    bar: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            padding: 15,
            font: {
              size: 12,
            },
          },
        },
        title: {
          display: false,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            font: {
              size: 12,
            },
          },
        },
        x: {
          ticks: {
            font: {
              size: 12,
            },
          },
        },
      },
    },
    line: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            padding: 15,
            font: {
              size: 12,
            },
          },
        },
        title: {
          display: false,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            font: {
              size: 12,
            },
          },
        },
        x: {
          ticks: {
            font: {
              size: 12,
            },
          },
        },
      },
    },
    pie: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            padding: 15,
            font: {
              size: 12,
            },
          },
        },
        title: {
          display: false,
        },
      },
    },
  };

  // Combinar opciones por defecto con opciones personalizadas
  const opciones = {
    ...opcionesPorDefecto[tipoGrafico],
    ...opcionesPersonalizadas,
  };

  // Función para descargar el gráfico como imagen
  const descargarGrafico = () => {
    const chart = chartRef.current;
    if (chart) {
      const url = chart.toBase64Image();
      const link = document.createElement("a");
      link.download = `${titulo.replace(/\s+/g, "_")}.png`;
      link.href = url;
      link.click();
    }
  };

  // Renderizar el componente de gráfico según el tipo
  const renderGrafico = () => {
    if (!datosGrafico || !datosGrafico.labels || !datosGrafico.datasets) {
      return (
        <SuiBox p={4} textAlign="center">
          <SuiTypography variant="body2" textColor="text">
            No hay datos disponibles para mostrar el gráfico
          </SuiTypography>
        </SuiBox>
      );
    }

    const componenteGrafico = {
      bar: <Bar ref={chartRef} data={datosGrafico} options={opciones} />,
      line: <Line ref={chartRef} data={datosGrafico} options={opciones} />,
      pie: <Pie ref={chartRef} data={datosGrafico} options={opciones} />,
    };

    return componenteGrafico[tipoGrafico] || componenteGrafico.bar;
  };

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "70vh",
        },
      }}
    >
      <DialogTitle>
        <SuiBox display="flex" justifyContent="space-between" alignItems="center">
          <SuiTypography variant="h5">{titulo}</SuiTypography>
          <IconButton onClick={onCerrar} size="small">
            <CloseIcon />
          </IconButton>
        </SuiBox>
      </DialogTitle>

      <DialogContent>
        <SuiBox
          p={2}
          sx={{ height: "55vh", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          {renderGrafico()}
        </SuiBox>
      </DialogContent>

      <DialogActions>
        <Tooltip title="Descargar gráfico como imagen">
          <SuiButton
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={descargarGrafico}
          >
            Descargar Imagen
          </SuiButton>
        </Tooltip>
        <SuiButton variant="gradient" color="secondary" size="small" onClick={onCerrar}>
          Cerrar
        </SuiButton>
      </DialogActions>
    </Dialog>
  );
}

ModalGrafico.propTypes = {
  abierto: PropTypes.bool.isRequired,
  onCerrar: PropTypes.func.isRequired,
  titulo: PropTypes.string,
  tipoGrafico: PropTypes.oneOf(["bar", "line", "pie"]),
  datosGrafico: PropTypes.shape({
    labels: PropTypes.array,
    datasets: PropTypes.array,
  }),
  opcionesPersonalizadas: PropTypes.object,
};

export default ModalGrafico;
